import { useEffect, useRef } from 'react'
import type { ReactElement } from 'react'

/** Google Identity Services, loaded by the script below. */
export const GOOGLE_IDENTITY_SCRIPT = 'https://accounts.google.com/gsi/client'

type GoogleCredentialResponse = { credential: string }

type GoogleIdentityServices = {
  accounts: {
    id: {
      initialize: (config: { client_id: string; callback: (response: GoogleCredentialResponse) => void }) => void
      renderButton: (parent: HTMLElement, options: Record<string, string>) => void
    }
  }
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  (typeof value === 'object' && value !== null) || typeof value === 'function'

/**
 * A guard and not a cast: `window.google` is whatever the script put there —
 * or nothing, or something else entirely if another script claimed the name.
 */
const isGoogleIdentityServices = (value: unknown): value is GoogleIdentityServices => {
  if (!isRecord(value) || !isRecord(value.accounts) || !isRecord(value.accounts.id)) {
    return false
  }
  const { initialize, renderButton } = value.accounts.id
  return typeof initialize === 'function' && typeof renderButton === 'function'
}

const loadedGoogleIdentityServices = (): GoogleIdentityServices | null => {
  const candidate: unknown = Reflect.get(window, 'google')
  return isGoogleIdentityServices(candidate) ? candidate : null
}

export type GoogleSignInButtonProps = {
  clientId: string
  /** Called with the ID token Google hands back, for `auth.loginWithGoogle`. */
  onCredential: (idToken: string) => void
}

/**
 * Google's own sign-in button (Google Identity Services), rendered into a box of
 * ours. The script is loaded only when this mounts — which the login page does
 * only when the API says this deploy has a Google client — so a deploy without
 * one never talks to Google at all.
 *
 * The ID token goes to `auth.loginWithGoogle` and nowhere else: it is not
 * stored, the session that comes back is the same httpOnly cookie pair as the
 * password login.
 */
export const GoogleSignInButton = ({ clientId, onCredential }: GoogleSignInButtonProps): ReactElement => {
  const containerRef = useRef<HTMLDivElement>(null)
  // The latest callback, read when Google answers: re-initialising Google on
  // every render of the page would re-render its button each time.
  const onCredentialRef = useRef(onCredential)
  onCredentialRef.current = onCredential

  /**
   * Initialises Google Identity Services and draws its button once the script
   * is there — at once if a previous visit already loaded it, on `load`
   * otherwise. The script tag stays in the page on unmount: unloading Google's
   * library is not something it supports.
   */
  useEffect(() => {
    let cancelled = false

    const render = (): void => {
      const google = loadedGoogleIdentityServices()
      const container = containerRef.current
      if (cancelled || google === null || container === null) {
        return
      }
      google.accounts.id.initialize({
        client_id: clientId,
        callback: (response) => onCredentialRef.current(response.credential),
      })
      google.accounts.id.renderButton(container, {
        theme: 'outline',
        size: 'large',
        text: 'signin_with',
        locale: 'es',
      })
    }

    if (loadedGoogleIdentityServices() !== null) {
      render()
      return () => {
        cancelled = true
      }
    }

    const existing = document.querySelector<HTMLScriptElement>(`script[src="${GOOGLE_IDENTITY_SCRIPT}"]`)
    const script = existing ?? document.createElement('script')
    script.addEventListener('load', render)
    if (existing === null) {
      script.src = GOOGLE_IDENTITY_SCRIPT
      script.async = true
      document.head.appendChild(script)
    }

    return () => {
      cancelled = true
      script.removeEventListener('load', render)
    }
  }, [clientId])

  return <div ref={containerRef} role="group" aria-label="Entrar con Google" />
}
